import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  GapAreaSeries,
  GapSubPeriodSummary,
  GapTimePoint,
  GapTradeComponent,
} from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'
import {
  GapRangeType,
  GapRepresentativeValue,
} from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'

import { GAP_CATEGORY_COLOR_MAP } from '~/constants/chartPalette'
import { decimalToNumberOr } from '~/models/decimal'
import {
  type BreakdownMode,
  supplyCategoryDataKey,
} from '~/pages/SupplyGap/models/supplyCategories'

/** フィルタ: 表示範囲 (= x 軸粒度) */
export const RANGE_TYPES = ['day', 'week', 'month'] as const
export type RangeType = (typeof RANGE_TYPES)[number]

/** localStorage 等の外部由来値を RangeType に絞り込む (廃止済み 'year' 等を弾く) */
export function isRangeType(v: unknown): v is RangeType {
  return (RANGE_TYPES as readonly string[]).includes(v as string)
}

/** フィルタ全体 */
export type GapFilterState = {
  rangeType: RangeType
  /** 対象日 (rangeType=day 時) YYYY-MM-DD */
  dayDate: string
  /** 開始週 (rangeType=week 時) YYYY-MM-DD (土曜 snap) */
  weekStart: string
  /** 週数 (rangeType=week 時) */
  weekDur: number
  /** 開始月 (rangeType=month 時) YYYY-MM-DD (月初 snap) */
  monthStart: string
  /** 月数 (rangeType=month 時) */
  monthDur: number
  /** 代表値 */
  representativeValue: GapRepresentativeValue
  /** 供給内訳の表示モード ('aggregate' = 供給力合計 1 本 / 'detail' = 受渡区分別の積上げ) */
  breakdownMode: BreakdownMode
  /**
   * 選択中のエリアコード。 **空 = 全業務エリア** (design `supply-gap.html` L1197-1234
   * のポジティブ選択モデル準拠。 2026-08-12 デザイナー判断で checkbox 群から
   * multi-select へ移行した際に導入)。 実際にクエリ・表示へ使う値は
   * [[resolveQueryAreaCodes]] で展開してから使うこと (この値をそのまま
   * 「表示中のエリア」として使わない)。
   */
  selectedAreaCodes: AreaCode[]
}

/** proto GapTimePoint から変換した表示行 (read-only なので updatedAt 不要) */
export type GapTimePointRow = {
  timeLabel: string
  supply: GapTradeComponent[]
  supplyTotalMw: number
  demandForecastMw: number
  sales: GapTradeComponent[]
  demandPlusSalesMw: number
  positionMw: number
}

/** proto GapAreaSeries から変換した表示行 */
export type GapAreaSeriesRow = {
  areaCode: AreaCode
  points: GapTimePointRow[]
  subPeriodSummaries: GapSubPeriodSummary[]
}

// proto からの変換ユーティリティ ---

export function convertTimePoint(pt: GapTimePoint): GapTimePointRow {
  return {
    timeLabel: pt.timeLabel,
    supply: pt.supply,
    supplyTotalMw: decimalToNumberOr(pt.supplyTotalMw),
    demandForecastMw: decimalToNumberOr(pt.demandForecastMw),
    sales: pt.sales,
    demandPlusSalesMw: decimalToNumberOr(pt.demandPlusSalesMw),
    positionMw: decimalToNumberOr(pt.positionMw),
  }
}

export function convertAreaSeries(series: GapAreaSeries): GapAreaSeriesRow {
  return {
    areaCode: series.areaCode,
    points: series.points.map(convertTimePoint),
    subPeriodSummaries: series.subPeriodSummaries,
  }
}

/** RangeType → GapRangeType 変換 */
export function toProtoRangeType(r: RangeType): GapRangeType {
  switch (r) {
    case 'day':
      return GapRangeType.DAY
    case 'week':
      return GapRangeType.MONTH // week は MONTH range として backend に渡す (下記 isWeekMode 参照)
    case 'month':
      return GapRangeType.MONTH
  }
}

/** rangeType=week は MONTH range で送る (proto は DAY/MONTH/YEAR 3 択)。
 *  週間モードでは period_from/to を week start から weekDur*7 日分に設定する。
 *  注: 現状は週起算の期間を「日次 point で描画」する (= 週次ロールアップでは
 *  ない。 weekDur=3 なら 21 本の日次棒)。 週ごとに代表値 1 本へ集約するかは
 *  ドメイン判断が要るため #1378 に分離。 */
export function isWeekMode(r: RangeType): boolean {
  return r === 'week'
}

/** ポジション > 0 = ロング (余剰供給) */
export function isLong(positionMw: number): boolean {
  return positionMw >= 0
}

/**
 * 系列色の安定マッピング。定義本体は `~/constants/chartPalette` に集約 (D-1)。
 * CATEGORY_COLOR_MAP は既存の import 経路 (`~/pages/SupplyGap/components/*`) を
 * 変えないための re-export。
 */
export const CATEGORY_COLOR_MAP: Record<string, string> = GAP_CATEGORY_COLOR_MAP

/**
 * メインチャート (供給 Bar + 需要 2 線) 用の Recharts データ点。
 *
 * 集約キー (`timeLabel` / `supplyTotal` / `demandForecast` / `demandPlusSales` /
 * `position`) は **表示モードに関わらず常に持つ**。 需要 2 線と tooltip の 2 大計は
 * 内訳表示でも描くので、 内訳の時だけ落とすと tooltip が undefined を出す。
 *
 * カテゴリ別 MW は `cat:<category_code>` のキーで同じ階層へ平坦化する
 * (Recharts の `dataKey` にそのまま渡すため)。 prefix を付けるのは、
 * `category_code` が backend 由来の任意文字列で、 素のままだと上の予約キーと
 * 衝突した時に数値を黙って上書きするため (キーの名前空間を分ける)。
 */
export type GapMainChartPoint = {
  timeLabel: string
  supplyTotal: number
  demandForecast: number
  demandPlusSales: number
  /** ギャップ (= backend 計算の position。tooltip 表示用、フロントで再計算しない) */
  position: number
  /**
   * カテゴリ別 MW。 キーは [[supplyCategoryDataKey]] が作る `cat:<category_code>`。
   * 表示中のカテゴリでもそのコマに値が無い (backend が 0 のカテゴリを出さない) ので
   * `undefined` を含める。 読み出し側の `?? 0` / `!= null` はこの型が要求する防御。
   */
  [categoryKey: `cat:${string}`]: number | undefined
}

/**
 * メインチャート (design `_renderMainInto`) 用データ生成。
 *
 * backend 集計済みの供給力合計 (supplyTotalMw) と同じコマの供給内訳
 * (カテゴリ別キー) を **両方** 載せる。 どちらを描くかは系列記述子
 * (`buildSupplySeries`) が決めるので、 ここでは表示モードを見ない
 * (Recharts は参照されないキーを無視する)。
 * 需要想定 / 需要想定+販売 は常に付随する (design 同様 Line overlay)。
 */
export function buildMainChartPoints(
  points: GapTimePointRow[],
): GapMainChartPoint[] {
  return points.map((pt) => {
    const entry: GapMainChartPoint = {
      timeLabel: pt.timeLabel,
      supplyTotal: pt.supplyTotalMw,
      demandForecast: pt.demandForecastMw,
      demandPlusSales: pt.demandPlusSalesMw,
      position: pt.positionMw,
    }

    for (const c of pt.supply) {
      const key = supplyCategoryDataKey(c.categoryCode)
      entry[key] = (entry[key] ?? 0) + decimalToNumberOr(c.valueMw)
    }
    return entry
  })
}

/**
 * メインチャートの Y 軸レンジ計算に渡す値。 集約表示 ({@link buildMainChartPoints}
 * の supplyTotal / demandForecast / demandPlusSales) の 3 系列に加え、 内訳表示
 * (`cat:<category_code>` の積上げ) の**同コマ総和**を返す。 列挙は
 * {@link buildMainChartPoints} と共有していないので、 描く系列を増減するときは
 * 両方を直す (集合がずれると軸が系列を上端で切り落とすが、 型では落ちない)。
 * 両者が一致することは `mainChartAxis.test.ts` の F1 で固定する。
 *
 * 積上げ総和を足すのは、 backend が「同じコマの category 値の総和 == supplyTotalMw」
 * を保って返す (集計は `supply_demand_gap_buckets.go` の accumulator が supplyTotal と
 * 同じ加算経路で行う) という **seam を越えた不変条件に軸の妥当性を預けない** ため。
 * 不変条件が保たれている限りこの値は supplyTotalMw と一致して軸を変えず、 破れた時
 * だけ軸が積上げの実高さまで伸びる (= 内訳の棒が軸上端で黙って切れない)。
 */
export function collectMainAxisValues(points: GapTimePointRow[]): number[] {
  return points.flatMap((pt) => {
    const values = [pt.supplyTotalMw, pt.demandForecastMw, pt.demandPlusSalesMw]
    if (pt.supply.length > 0) {
      values.push(
        pt.supply.reduce((sum, c) => sum + decimalToNumberOr(c.valueMw), 0),
      )
    }
    return values
  })
}

/** 差分チャート (design `_renderDiffInto` 相当) 用データ点。 ロング/ショートを別キーに
 *  分けて Bar 2 本に渡す (どちらか一方が常に 0)。 */
export type GapDiffChartPoint = {
  timeLabel: string
  long: number
  short: number
}

export function buildDiffChartPoints(
  points: GapTimePointRow[],
): GapDiffChartPoint[] {
  return points.map((pt) => ({
    timeLabel: pt.timeLabel,
    long: isLong(pt.positionMw) ? pt.positionMw : 0,
    short: isLong(pt.positionMw) ? 0 : pt.positionMw,
  }))
}

/** サマリ表 1 行 (design L312-347 準拠: エリア列を持つ横断表の 1 行)。 */
export type GapSummaryRow = {
  areaLabel: string
  periodLabel: string
  isLong: boolean
  baseMw: number
  peakMw: number
  offpeakMw: number
  /** 安全売却量 (余剰の最小、ロング区間のみ有意) */
  safeSellMw: number
  /** 必要買い量 (不足の最大、ショート区間のみ有意) */
  requiredBuyMw: number
}

/**
 * 複数エリアの subPeriodSummaries をエリア列付きの単一表用に平坦化する。
 * design (`_renderSummary`) は全エリアを 1 表にまとめるため (= per-area 表を止め、
 * エリアを列として持つ)、 表示側は本関数の戻り値をそのまま描画すればよい。
 */
export function buildSummaryRows(
  rows: GapAreaSeriesRow[],
  areaCodeLabel: (code: AreaCode) => string,
): GapSummaryRow[] {
  return rows.flatMap((r) =>
    r.subPeriodSummaries.map((s) => ({
      areaLabel: areaCodeLabel(r.areaCode),
      periodLabel: s.periodLabel,
      isLong: s.isLong,
      baseMw: decimalToNumberOr(s.baseMw),
      peakMw: decimalToNumberOr(s.peakMw),
      offpeakMw: decimalToNumberOr(s.offpeakMw),
      safeSellMw: decimalToNumberOr(s.safeSellMw),
      requiredBuyMw: decimalToNumberOr(s.requiredBuyMw),
    })),
  )
}

/** CSV 用データ生成。
 * 現状は表示中 response の各 point をそのまま行展開する (= 画面に表示中の
 * 代表値・期間に従う)。 出力粒度別 (base/peak/offpeak/slot48) の行生成は
 * 未実装で、 format 引数を足して粒度別に出し分けるのは #1362 で対応する。
 */
export function buildCsvRows(
  rows: GapAreaSeriesRow[],
  areaCodeLabel: (code: AreaCode) => string,
): string {
  const header = [
    'エリア',
    '時刻',
    '供給合計(MW)',
    '需要想定(MW)',
    '需要+販売(MW)',
    'ポジション(MW)',
  ].join(',')

  const body = rows.flatMap((r) =>
    r.points.map((pt) =>
      [
        areaCodeLabel(r.areaCode),
        pt.timeLabel,
        pt.supplyTotalMw.toFixed(2),
        pt.demandForecastMw.toFixed(2),
        pt.demandPlusSalesMw.toFixed(2),
        pt.positionMw.toFixed(2),
      ].join(','),
    ),
  )

  return [header, ...body].join('\n')
}

/**
 * `GapFilterState.selectedAreaCodes` (空 = 全業務エリア) を、 実際にクエリ・
 * 表示 (CSV エリアラベル / 単一エリアレイアウト判定等) へ使う「実効エリア一覧」に
 * 展開する。 空でなければそのまま返す。
 *
 * 業務エリア一覧の出どころは呼び出し側 (`businessAreaCodes`、 personal settings
 * 由来) を渡してもらう (= 本関数は独自のエリア一覧を持たない)。
 */
export function resolveQueryAreaCodes(
  selectedAreaCodes: AreaCode[],
  businessAreaCodes: AreaCode[],
): AreaCode[] {
  return selectedAreaCodes.length > 0 ? selectedAreaCodes : businessAreaCodes
}

/** 日付ヘルパ: 直近の土曜 (週間計画 = 土曜起算) に snap */
export function snapToSaturday(d: Date): Date {
  const result = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  // 土曜 = 6
  const dow = result.getDay()
  result.setDate(result.getDate() - ((dow - 6 + 7) % 7))
  return result
}

/** 日付ヘルパ: 月初に snap */
export function snapToMonthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

/** YYYY-MM-DD 文字列から Date を生成 (UTC 解釈を避ける) */
export function parseYmd(s: string): Date | null {
  const parts = s.split('-')
  if (parts.length !== 3) return null
  const d = new Date(+parts[0], +parts[1] - 1, +parts[2])
  // 数値化できない断片 ('20xx-1a-3b' 等) は Invalid Date になる。 そのまま返すと
  // 呼び出し側の `?? now` フォールバックを素通りし、 NaN の proto Date を BFF へ
  // 送る (= 形式不正は null という契約を守る)。
  return Number.isNaN(d.getTime()) ? null : d
}

/** Date を YYYY-MM-DD に変換 */
export function toYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
